"use client";

import { Loader2 } from "lucide-react";
import { useParams } from "next/navigation";
import type { FormEvent } from "react";
import {
  type ToolStrings,
  useToolStrings,
} from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { EditableEntry } from "@/lib/citation";
import { MANUAL_SOURCE_TYPE_OPTIONS } from "@/lib/source-types";

type ManualEntry = EditableEntry;

// A whole author field in braces is one group author, kept verbatim ("{R Core Team}").
const WHOLE_GROUP_AUTHOR_REGEX = /^\s*\{([^{}]*)\}\s*$/;

type FieldLabelKey = keyof Pick<
  ToolStrings,
  | "fieldTranslators"
  | "fieldInterviewers"
  | "fieldVersion"
  | "fieldCourt"
  | "fieldIssuingAuthority"
  | "fieldPatentNumber"
  | "fieldEpisodeNumber"
  | "fieldLawNumber"
  | "fieldReportNumber"
  | "fieldReviewedTitle"
  | "fieldReviewedAuthors"
>;

interface ContextField {
  field: keyof ManualEntry;
  label: FieldLabelKey;
  types: readonly string[];
  placeholder?: string;
}

// Fields that only some source types use, shown for those types alone so the form stays
// short. Each maps to the CSL variable of the same meaning (see editableEntryToCsl).
const CONTEXT_FIELDS: readonly ContextField[] = [
  {
    field: "translator",
    label: "fieldTranslators",
    types: [
      "book",
      "chapter",
      "collection",
      "article-journal",
      "article-magazine",
      "article-newspaper",
      "entry-encyclopedia",
      "entry-dictionary",
      "manuscript",
      "report",
    ],
    placeholder: "e.g. Richard Pevear",
  },
  {
    field: "interviewer",
    label: "fieldInterviewers",
    types: ["interview"],
    placeholder: "e.g. Terry Gross",
  },
  {
    field: "version",
    label: "fieldVersion",
    types: ["software", "dataset"],
    placeholder: "e.g. 4.3.1",
  },
  {
    field: "authority",
    label: "fieldCourt",
    types: ["legal_case"],
    placeholder: "e.g. D. Kan. or 9th Cir.",
  },
  {
    field: "authority",
    label: "fieldIssuingAuthority",
    types: ["patent"],
    placeholder: "e.g. U.S. Patent and Trademark Office",
  },
  {
    field: "number",
    label: "fieldPatentNumber",
    types: ["patent"],
    placeholder: "e.g. 174,465",
  },
  {
    field: "number",
    label: "fieldEpisodeNumber",
    types: ["broadcast"],
    placeholder: "e.g. 7",
  },
  {
    field: "number",
    label: "fieldLawNumber",
    types: ["legislation", "bill"],
    placeholder: "e.g. Pub. L. No. 111-148 or H.R. 3590",
  },
  {
    field: "number",
    label: "fieldReportNumber",
    types: ["report"],
    placeholder: "e.g. NCES 2020-144",
  },
  {
    field: "reviewedTitle",
    label: "fieldReviewedTitle",
    types: ["review", "review-book"],
  },
  {
    field: "reviewedAuthor",
    label: "fieldReviewedAuthors",
    types: ["review", "review-book"],
  },
];

const DEFAULT_SOURCE_TYPE = MANUAL_SOURCE_TYPE_OPTIONS[0].cslType;

// The "container" field is the journal for an article but the BOOK for a chapter
// (issue #126). Adapt its label/placeholder so the book-chapter form reads correctly
// without changing the Journal/Newspaper/Magazine labels.
function getContainerCopy(
  type: string | undefined,
  s: ToolStrings
): {
  label: string;
  placeholder: string;
} {
  if (type === "chapter") {
    return {
      label: s.fieldBookTitle,
      placeholder: s.fieldBookTitleHint,
    };
  }
  return {
    label: s.fieldJournal,
    placeholder: s.fieldJournalHint,
  };
}

interface ManualEntryFormProps {
  entry: ManualEntry;
  onChange: (entry: ManualEntry) => void;
  onSubmit: (e: FormEvent) => void;
  loading: boolean;
}

export function ManualEntryForm({
  entry,
  onChange,
  onSubmit,
  loading,
}: ManualEntryFormProps) {
  const s = useToolStrings();
  const params = useParams<{ locale?: string }>();
  const locale = params?.locale ?? "en";
  const updateField = (field: keyof ManualEntry, value: string) => {
    onChange({ ...entry, [field]: value });
  };

  const containerCopy = getContainerCopy(entry.type, s);
  const sourceType = entry.type ?? DEFAULT_SOURCE_TYPE;
  const contextFields = CONTEXT_FIELDS.filter((config) =>
    config.types.includes(sourceType)
  );
  const groupAuthor = WHOLE_GROUP_AUTHOR_REGEX.exec(entry.author ?? "");
  const toggleGroupAuthor = (checked: boolean) => {
    const author = entry.author ?? "";
    if (checked) {
      updateField("author", author.trim() ? `{${author.trim()}}` : "{}");
    } else {
      updateField("author", groupAuthor ? groupAuthor[1] : author);
    }
  };

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      {locale === "en" ? (
        <p className="text-muted-foreground text-sm">
          Enter the details you know — we&apos;ll format the citation for you.
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="manual-type">{s.sourceType}</Label>
          <Select
            onValueChange={(value) => updateField("type", value)}
            value={entry.type ?? DEFAULT_SOURCE_TYPE}
          >
            <SelectTrigger className="w-full" id="manual-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MANUAL_SOURCE_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.cslType} value={option.cslType}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="manual-title">{s.fieldTitleShort} *</Label>
          <Input
            aria-required="true"
            id="manual-title"
            onChange={(e) => updateField("title", e.target.value)}
            placeholder={s.fieldTitle}
            required
            value={entry.title ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-author">{s.fieldAuthors}</Label>
          <Input
            id="manual-author"
            onChange={(e) =>
              updateField(
                "author",
                groupAuthor ? `{${e.target.value}}` : e.target.value
              )
            }
            placeholder={s.fieldAuthorsHint}
            value={groupAuthor ? groupAuthor[1] : (entry.author ?? "")}
          />
          <label
            className="flex items-center gap-2 text-muted-foreground text-xs"
            htmlFor="manual-author-group"
          >
            <input
              aria-describedby="manual-author-group-hint"
              checked={Boolean(groupAuthor)}
              className="size-3.5 accent-foreground"
              id="manual-author-group"
              onChange={(e) => toggleGroupAuthor(e.target.checked)}
              type="checkbox"
            />
            {s.fieldAuthorsGroup}
          </label>
          <p
            className="text-muted-foreground text-xs"
            id="manual-author-group-hint"
          >
            {s.fieldAuthorsGroupHint}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-date">{s.fieldDate}</Label>
          <Input
            id="manual-date"
            onChange={(e) => updateField("issued", e.target.value)}
            placeholder="2024 or 2024-01-15"
            value={entry.issued ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-publisher">{s.fieldPublisher}</Label>
          <Input
            id="manual-publisher"
            onChange={(e) => updateField("publisher", e.target.value)}
            placeholder={s.fieldPublisherHint}
            value={entry.publisher ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-url">URL</Label>
          <Input
            id="manual-url"
            onChange={(e) => updateField("URL", e.target.value)}
            placeholder="https://..."
            type="url"
            value={entry.URL ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-container">{containerCopy.label}</Label>
          <Input
            id="manual-container"
            onChange={(e) => updateField("containerTitle", e.target.value)}
            placeholder={containerCopy.placeholder}
            value={entry.containerTitle ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-editor">{s.fieldEditors}</Label>
          <Input
            id="manual-editor"
            onChange={(e) => updateField("editor", e.target.value)}
            placeholder="e.g. Cross, Nigel (for an edited book)"
            value={entry.editor ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-doi">DOI</Label>
          <Input
            id="manual-doi"
            onChange={(e) => updateField("DOI", e.target.value)}
            placeholder="10.1000/xyz123"
            value={entry.DOI ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-volume">{s.fieldVolume}</Label>
          <Input
            id="manual-volume"
            onChange={(e) => updateField("volume", e.target.value)}
            placeholder="e.g. 12"
            value={entry.volume ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-issue">{s.fieldIssue}</Label>
          <Input
            id="manual-issue"
            onChange={(e) => updateField("issue", e.target.value)}
            placeholder="e.g. 3"
            value={entry.issue ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-page">{s.fieldPages}</Label>
          <Input
            id="manual-page"
            onChange={(e) => updateField("page", e.target.value)}
            placeholder="e.g. 45-67"
            value={entry.page ?? ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-isbn">ISBN</Label>
          <Input
            id="manual-isbn"
            onChange={(e) => updateField("ISBN", e.target.value)}
            placeholder="978-0-123456-78-9"
            value={entry.ISBN ?? ""}
          />
        </div>
        {contextFields.map((config) => (
          <div className="space-y-2" key={`${config.field}-${config.label}`}>
            <Label htmlFor={`manual-${config.field}`}>{s[config.label]}</Label>
            <Input
              id={`manual-${config.field}`}
              onChange={(e) => updateField(config.field, e.target.value)}
              placeholder={config.placeholder}
              value={entry[config.field] ?? ""}
            />
          </div>
        ))}
        <div className="space-y-2">
          <Label htmlFor="manual-genre">{s.fieldFormat}</Label>
          <Input
            id="manual-genre"
            onChange={(e) => updateField("genre", e.target.value)}
            placeholder="e.g. Photograph, Infographic, Map"
            value={entry.genre ?? ""}
          />
        </div>
      </div>
      <Button disabled={loading} type="submit">
        {loading ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {s.generating}
          </>
        ) : (
          s.generate
        )}
      </Button>
    </form>
  );
}
